import { ComponentType, createContext, ReactNode, useContext } from 'react'
import { useProcessBase, useProcessMessagesBase, useProcessStateBase } from './base-hooks'
import type { DepsMap, DepsState, Process, ProcessMessage, ProcessModule } from './process'
import { StoreContext } from './process'
import type { ReduxStore } from './redux-store'

/* ------------------------------------------------------------------ */
/* 1.  Re-usable function-type aliases                                */
/* ------------------------------------------------------------------ */

export type UseProcessState = <PS, Msg extends ProcessMessage, D extends DepsMap = {}, Sel = PS>(
  module: ProcessModule<PS, Msg, any, any, D>,
  selector: (ps: PS, deps: DepsState<D>) => Sel
) => Sel

export type UseProcess = <PS, Msg extends ProcessMessage, Arg = void, D extends DepsMap = {}>(
  module: ProcessModule<PS, Msg, Arg, any, D>,
  initArg?: Arg
) => Process<PS, Msg>

export type UseProcessMessages = <PS, Msg extends ProcessMessage>(
  module: ProcessModule<PS, Msg, any, any>,
  handler: (msg: Msg) => void
) => void

type StoreProviderProps = {
  store: ReduxStore
  context?: StoreContext
  children?: ReactNode
}

/* ------------------------------------------------------------------ */
/* 2.  The environment bundle returned by `createStoreProvider`       */
/* ------------------------------------------------------------------ */

export interface StoreProviderEnvironment {
  StoreProvider: ComponentType<StoreProviderProps>
  useStore: () => ReduxStore
  useProcessState: UseProcessState
  useProcess: UseProcess
  useProcessMessages: UseProcessMessages
}

/* ------------------------------------------------------------------ */
/* 3.  Factory signature                                              */
/* ------------------------------------------------------------------ */

export function createStoreProvider(): StoreProviderEnvironment {
  const StoreContext = createContext<{ store: ReduxStore; context: StoreContext } | undefined>(
    undefined
  )

  function StoreProvider({ children, store, context = {} }: StoreProviderProps) {
    return <StoreContext.Provider value={{ store, context }}>{children}</StoreContext.Provider>
  }

  function useStore(): ReduxStore {
    return useContext(StoreContext)!.store
  }

  function useStoreContext(): StoreContext {
    return useContext(StoreContext)!.context
  }

  function useProcessState<PS, Msg extends ProcessMessage, D extends DepsMap = {}, Sel = PS>(
    module: ProcessModule<PS, Msg, any, any, D>,
    selector: (ps: PS, deps: DepsState<D>) => Sel
  ) {
    const store = useStore()
    return useProcessStateBase(store, module, selector)
  }

  function useProcess<PS, Msg extends ProcessMessage, Arg = void, D extends DepsMap = {}>(
    module: ProcessModule<PS, Msg, Arg, any, D>,
    initArg?: Arg
  ) {
    const store = useStore()
    return useProcessBase(store, useStoreContext(), module, initArg)
  }

  function useProcessMessages<PS, Msg extends ProcessMessage>(
    module: ProcessModule<PS, Msg, any, any>,
    handler: (msg: Msg) => void
  ) {
    const store = useStore()
    return useProcessMessagesBase(store, module, handler)
  }

  return {
    StoreProvider: StoreProvider,
    useStore: useStore,
    useProcessState: useProcessState,
    useProcess: useProcess,
    useProcessMessages: useProcessMessages
  }
}
